import type { IAvailableIcons } from '@/components/common/Icon'
import type { IResourceType } from '@/types/resource'

export const ALERT_STATUS = {
  CLOSED: 'closed',
  OPEN: 'open',
}

export const DOCUMENT_STATUS = {
  NOT_APPLICABLE: 'notApplicable' as const,
  NOT_STARTED: 'notStarted' as const,
  IN_PROGRESS: 'inProgress' as const,
  COMPLETED: 'completed' as const,
  APPROVED: 'approved' as const,
}

export const CONTROL_STATUS = {
  NOT_APPLICABLE: 'notApplicable' as const,
  NOT_STARTED: 'notStarted' as const,
  IN_PROGRESS: 'inProgress' as const,
  COMPLETED: 'completed' as const,
}

export const EVIDENCE_STATUS = {
  NOT_APPLICABLE: 'notApplicable' as const,
  NOT_STARTED: 'notStarted' as const,
  IN_PROGRESS: 'inProgress' as const,
  COMPLETED: 'completed' as const,
}

export const RISK_STATUS = {
  IN_PROGRESS: 'inProgress' as const,
  PLANNED: 'planned' as const,
  DELAYED: 'delayed' as const,
  CLOSED: 'closed' as const,
}

export const TASK_STATUS = {
  NOT_STARTED: 'notStarted' as const,
  IN_PROGRESS: 'inProgress' as const,
  COMPLETED: 'completed' as const,
  IN_REVIEW: 'inReview' as const,
}

export const NON_CONFORMITY_STATUS = {
  NOT_STARTED: 'notStarted' as const,
  IN_PROGRESS: 'inProgress' as const,
  DELAYED: 'delayed' as const,
  COMPLETED: 'completed' as const,
}

export const CONNECTOR_STATUS = {
  NOT_CONNECTED: 'notStarted' as const,
  CONNECTED: 'completed' as const,
  ERROR: 'error' as const,
}

export const FINDINGS_STATUS = {
  OPEN: 'open',
  CLOSED: 'closed',
  MUTED: 'muted',
} as const

export const QUESTIONNAIRE_STATUS = {
  SUBMITTED: 'SUBMITTED' as const,
  IN_PROGRESS: 'IN_PROGRESS' as const,
  READY: 'READY' as const,
  COMPLETED: 'COMPLETED' as const,
}

export const MDM_SOFTWARE_STATUS = {
  INSTALLED: 'installed',
  PENDING: 'pending',
  FAILED: 'failed',
} as const

export const MDM_SOFTWARE_HOST_STATUS = {
  INSTALLED: 'installed',
  PENDING: 'pending',
  FAILED: 'failed',
  AVAILABLE: 'available',
} as const

export const MDM_SOFTWARE_SOURCE = {
  FLEET_MAINTAINED: 'fleet_maintained',
  APP_STORE: 'app_store',
  CUSTOM: 'custom',
} as const

export const MDM_SCRIPT_RESULT_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
  NOT_RUN: 'not_run',
} as const

export const MDM_SCRIPT_RUN_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  FINISHED: 'finished',
  FAILED: 'failed',
  CANCELED: 'canceled',
} as const

export const MDM_SCRIPT_HOST_RESULT_STATUS = {
  PENDING: 'pending',
  RAN: 'ran',
  ERRORED: 'errored',
  INCOMPATIBLE: 'incompatible',
  CANCELED: 'canceled',
} as const

export const MDM_PROFILE_DEPLOYMENT_STATUS = {
  DEPLOYED: 'deployed',
  VERIFIED: 'verified',
  VERIFYING: 'verifying',
  PENDING: 'pending',
  FAILED: 'failed',
  REMOVED: 'removed',
} as const

export const MDM_PROFILE_HOST_STATUS = {
  VERIFIED: 'verified',
  VERIFYING: 'verifying',
  PENDING: 'pending',
  FAILED: 'failed',
} as const

export const CASE_STATUS = {
  OPEN: 'open' as const,
  IN_PROGRESS: 'in_progress' as const,
  CLOSED: 'closed' as const,
} as const

// draft -> completed -> approved. `completed` means drafting is finished and the
// version is waiting for approval; its content is still editable.
export const TEXT_DOCUMENT_VERSION_STATUS = {
  DRAFT: 'draft' as const,
  COMPLETED: 'completed' as const,
  APPROVED: 'approved' as const,
} as const

export const CHAT_TOOL_CALL_STATUS = {
  SUCCESS: 'success' as const,
  ERROR: 'error' as const,
} as const

export type IChatToolCallStatus = (typeof CHAT_TOOL_CALL_STATUS)[keyof typeof CHAT_TOOL_CALL_STATUS]

export const STATUS_MAP: { [key in IResourceType]: string[] } = {
  document: Object.values(DOCUMENT_STATUS),
  control: Object.values(CONTROL_STATUS),
  evidence: Object.values(EVIDENCE_STATUS),
  risk: Object.values(RISK_STATUS),
  task: Object.values(TASK_STATUS),
  employee: [],
  nonConformity: Object.values(NON_CONFORMITY_STATUS),
}

export const ALL_STATUSES = [
  ...Object.values(TASK_STATUS),
  ...Object.values(DOCUMENT_STATUS),
  ...Object.values(CONTROL_STATUS),
  ...Object.values(EVIDENCE_STATUS),
  ...Object.values(RISK_STATUS),
  ...Object.values(NON_CONFORMITY_STATUS),
  ...Object.values(FINDINGS_STATUS),
  ...Object.values(CONNECTOR_STATUS),
] as const

export const STATUS_ICONS: { [key: string]: IAvailableIcons } = {
  notApplicable: 'circle',
  notStarted: 'autorenew',
  inProgress: 'hourglass-empty',
  completed: 'check',
  approved: 'approval',
  planned: 'autorenew',
  delayed: 'hourglass-empty',
  closed: 'close',
  inReview: 'search-outlined',
  notConnected: 'link-outlined',
  connected: 'link-outlined',
  error: 'close',
  connecting: 'hourglass-empty',
}
